var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '../icon/Icon.js';
/**
 * Pill-style segmented control with an animated underlay that slides to
 * the active option. Equal-width segments. Companion to <basic-tab>,
 * which keeps the underline-style for hierarchical navigation tabs.
 *
 * Emits `tab-change` with `{ value }` when the user picks a new segment.
 */
let SegmentedTabs = class SegmentedTabs extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.value = '';
        this.size = 'small';
    }
    _activeIndex() {
        const idx = this.options.findIndex((o) => o.value === this.value);
        return idx >= 0 ? idx : 0;
    }
    firstUpdated() {
        this._positionSlider();
        // Reposition on any layout change (container resize, web-font load that
        // reflows the labels), so the underlay tracks the segments precisely.
        this._resizeObserver = new ResizeObserver(() => this._positionSlider());
        const track = this.renderRoot.querySelector('.track');
        if (track)
            this._resizeObserver.observe(track);
    }
    updated(_changed) {
        this._positionSlider();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
    }
    /** Snap the blue underlay onto the active segment's exact box. Segments
     *  are unequal width (content-sized), so we read the live geometry rather
     *  than assume an even split. */
    _positionSlider() {
        const slider = this._sliderEl;
        const segments = this._segmentEls;
        if (!slider || !segments || segments.length === 0)
            return;
        const btn = segments[this._activeIndex()];
        if (!btn)
            return;
        slider.style.width = `${btn.offsetWidth}px`;
        slider.style.height = `${btn.offsetHeight}px`;
        slider.style.transform = `translate(${btn.offsetLeft}px, ${btn.offsetTop}px)`;
    }
    _onPick(value) {
        const opt = this.options.find((o) => o.value === value);
        if (opt?.disabled)
            return;
        if (value === this.value)
            return;
        this.value = value;
        this.dispatchEvent(new CustomEvent('tab-change', {
            detail: { value },
            bubbles: true,
            composed: true
        }));
    }
    render() {
        return html `
      <div class="track" role="tablist">
        <span class="slider" aria-hidden="true"></span>
        ${this.options.map((opt) => html `
            <button
              type="button"
              class="segment ${opt.value === this.value ? 'active' : ''}"
              role="tab"
              aria-selected=${opt.value === this.value}
              ?disabled=${opt.disabled ?? false}
              title=${opt.title ?? opt.label}
              @click=${() => this._onPick(opt.value)}
            >
              ${opt.iconName
            ? html `<span class="icon"><basic-icon .name=${opt.iconName}></basic-icon></span>`
            : opt.icon
                ? html `<span class="icon">${unsafeHTML(opt.icon)}</span>`
                : ''}
              <span>${opt.label}</span>
            </button>
          `)}
      </div>
    `;
    }
};
SegmentedTabs.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .track {
      position: relative;
      display: flex;
      gap: 2px;
      padding: 2px;
      background: #f0f3f7;
      border-radius: 8px;
      overflow: hidden;
    }

    .slider {
      /* Geometry (left/top via transform, width, height) is measured from
         the active segment in _positionSlider — segments are NOT equal width
         (flex items keep min-width:auto, so e.g. Evolution is wider than
         After), so a CSS-only calc would drift. */
      position: absolute;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      background: #0078d4;
      border-radius: 6px;
      transition: transform 0.2s, width 0.2s;
      z-index: 0;
    }

    .segment {
      position: relative;
      z-index: 1;
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 7px 10px;
      background: transparent;
      border: none;
      font: inherit;
      font-weight: 600;
      color: #4a5868;
      cursor: pointer;
      transition: color 0.2s;
      white-space: nowrap;
    }

    .segment:hover {
      color: #2a3440;
    }

    .segment.active,
    .segment.active:hover {
      color: #fff;
    }

    .segment:focus-visible {
      outline: 2px solid #0078d4;
      outline-offset: 2px;
      border-radius: 4px;
    }

    .segment[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
      color: #4a5868;
    }

    /* Sizes */
    :host([size='small']) .segment {
      font-size: 13px;
      padding: 7px 10px;
    }
    :host([size='medium']) .segment {
      font-size: 14px;
      padding: 9px 14px;
    }

    .icon {
      display: inline-flex;
      flex-shrink: 0;
    }
    .icon ::slotted(svg),
    .icon svg {
      width: 14px;
      height: 14px;
    }
  `;
__decorate([
    property({ type: Array })
], SegmentedTabs.prototype, "options", void 0);
__decorate([
    property({ type: String })
], SegmentedTabs.prototype, "value", void 0);
__decorate([
    property({ type: String })
], SegmentedTabs.prototype, "size", void 0);
__decorate([
    query('.slider')
], SegmentedTabs.prototype, "_sliderEl", void 0);
__decorate([
    queryAll('.segment')
], SegmentedTabs.prototype, "_segmentEls", void 0);
SegmentedTabs = __decorate([
    customElement('segmented-tabs')
], SegmentedTabs);
export { SegmentedTabs };
//# sourceMappingURL=SegmentedTabs.js.map